// @flow
// Renders /docs/products landing page
import * as React from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {Heading, Body, BodyAlt} from 'sail/Text';
import {UnstyledLink} from 'sail/UnstyledLink';
import {DocsIcon} from 'docs/components';
import {defineMessage, FormattedMessage} from 'react-intl';

import './ProductList.scss';

type ProductListItemProps = {|
  header: string,
  description: string,
  link: string,
  icon: string,
|};

const ProductListItem = ({
  header,
  description,
  link,
  icon,
}: ProductListItemProps) => {
  return (
    <UnstyledLink href={link} style={{display: 'inline-flex'}}>
      <Box
        flex={{direction: 'row', alignItems: 'center'}}
        margin={{vertical: 12}}
      >
        <DocsIcon
          category="product"
          icon={icon}
          size={32}
          className="ProductListItem-icon"
        />
        <Box flex={{direction: 'column'}} margin={{left: 16}}>
          <BodyAlt className="ProductListItem-header">
            <FormattedMessage {...header} />
          </BodyAlt>
          <Body>
            <FormattedMessage {...description} />
          </Body>
        </Box>
      </Box>
    </UnstyledLink>
  );
};

type ProductListSectionProps = {|
  heading: string,
  children: React.Node,
|};

const ProductListSection = ({heading, children}: ProductListSectionProps) => {
  return (
    <Box margin={{bottom: 20}}>
      <Box margin={{bottom: 12}}>
        <Heading className="ProductListSection-header">
          <FormattedMessage {...heading} />
        </Heading>
      </Box>
      <Group spacing={12}>{children} </Group>
    </Box>
  );
};

type ProductListProps = {|
  showBanking: boolean,
  showOpal: boolean,
  showCorporateCard: boolean,
  children: React.Node,
|};

// TODO(nav-cleanup): Translate strings in this component
// TODO: Use `Products` map instead of strings, see docs/util/products.js
export const ProductList = ({
  showBanking,
  showOpal,
  showCorporateCard,
  children,
}: ProductListProps) => {
  return (
    <Box className="ProductList">
      <Group flexBasis="50%">
        <Box className="ProductList-primary">
          <ProductListSection
            heading={defineMessage({
              id: 'docsApp.components.contrib.productList.directPayments',
              defaultMessage: 'Direct payments',
            })}
          >
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.payments',
                defaultMessage: 'Payments',
              })}
              icon="payments"
              link="/docs/payments"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.onlinePayments',
                defaultMessage: 'Online payments',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.invoicing',
                defaultMessage: 'Invoicing',
              })}
              icon="invoicing"
              link="/docs/invoicing"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.oneOffInvoices',
                defaultMessage: 'One-off invoices',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.billing',
                defaultMessage: 'Billing',
              })}
              icon="subscriptions"
              link="/docs/billing"
              description={defineMessage({
                id:
                  'docsApp.components.contrib.productList.subscriptionsAndRecurring',
                defaultMessage: 'Subscriptions and recurring payments',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.terminal',
                defaultMessage: 'Terminal',
              })}
              icon="terminal"
              link="/docs/terminal"
              description={defineMessage({
                id:
                  'docsApp.components.contrib.productList.inPersonAndOmnichannel',
                defaultMessage: 'In-person and omnichannel payments',
              })}
            />
          </ProductListSection>
          <ProductListSection
            heading={defineMessage({
              id: 'docsApp.components.contrib.productList.multipartyPayments',
              defaultMessage: 'Multiparty payments',
            })}
          >
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.connect',
                defaultMessage: 'Connect',
              })}
              icon="connect"
              link="/docs/connect"
              description={defineMessage({
                id:
                  'docsApp.components.contrib.productList.paymentsForPlatforms',
                defaultMessage: 'Payments for platforms and marketplaces',
              })}
            />
          </ProductListSection>
          <ProductListSection
            heading={defineMessage({
              id: 'docsApp.components.contrib.productList.prebuiltComponents',
              defaultMessage: 'Prebuilt components',
            })}
          >
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.paymentLinks',
                defaultMessage: 'Payment Links',
              })}
              icon="paymentlinks"
              link="/docs/payments/payment-links"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.noCodePayments',
                defaultMessage: 'No-code payments',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.checkout',
                defaultMessage: 'Checkout',
              })}
              icon="checkout"
              link="/docs/checkout"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.prebuiltCheckout',
                defaultMessage: 'Prebuilt, Stripe hosted checkout page',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.elements',
                defaultMessage: 'Elements',
              })}
              icon="elements"
              link="/docs/payments/elements"
              description={defineMessage({
                id:
                  'docsApp.components.contrib.productList.secureFrontendComponents',
                defaultMessage: 'Secure frontend UI components',
              })}
            />
          </ProductListSection>
        </Box>
      </Group>
      <Group flexBasis="50%">
        <Box className="ProductList-secondary">
          <ProductListSection
            heading={defineMessage({
              id: 'docsApp.components.contrib.productList.businessOperations',
              defaultMessage: 'Business operations',
            })}
          >
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.radar',
                defaultMessage: 'Radar',
              })}
              icon="radar"
              link="/docs/radar"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.fraudAndRisk',
                defaultMessage: 'Fraud and risk management',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.sigma',
                defaultMessage: 'Sigma',
              })}
              icon="sigma"
              link="/docs/sigma"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.customReports',
                defaultMessage: 'Custom reports',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.climate',
                defaultMessage: 'Climate',
              })}
              icon="climate"
              link="/docs/climate/faqs"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.removeCarbon',
                defaultMessage: 'Remove carbon as you grow your business',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.atlas',
                defaultMessage: 'Atlas',
              })}
              icon="atlas"
              link="/docs/atlas"
              description={defineMessage({
                id:
                  'docsApp.components.contrib.productList.startupIncorporation',
                defaultMessage: 'Startup incorporation',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.identity',
                defaultMessage: 'Identity',
              })}
              icon="identity"
              link="/docs/identity"
              description={defineMessage({
                id:
                  'docsApp.components.contrib.productList.identityVerification',
                defaultMessage: 'Identity verification',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.tax',
                defaultMessage: 'Tax',
              })}
              icon="tax"
              link="/docs/tax"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.taxCalculation',
                defaultMessage: 'Automatic tax calculation',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.revenue',
                defaultMessage: 'Revenue Recognition',
              })}
              icon="revrec"
              link="/docs/revenue-recognition"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.revenueDescription',
                defaultMessage: 'Accounting automation',
              })}
            />
            <ProductListItem
              header={defineMessage({
                id:
                  'docsApp.components.contrib.productList.financialConnections',
                defaultMessage: 'Financial Connections',
              })}
              icon="financialconnections"
              link="/docs/financial-connections"
              description={defineMessage({
                id:
                  'docsApp.components.contrib.productList.financialConnectionsDescription',
                defaultMessage: 'Linked financial account data',
              })}
            />
          </ProductListSection>
          <ProductListSection
            heading={defineMessage({
              id: 'docsApp.components.contrib.productList.financialServices',
              defaultMessage: 'Financial services',
            })}
          >
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.issuing',
                defaultMessage: 'Issuing',
              })}
              icon="issuing"
              link="/docs/issuing"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.cardCreation',
                defaultMessage: 'Card creation',
              })}
            />
            {showBanking && (
              <ProductListItem
                header={defineMessage({
                  id: 'docsApp.components.contrib.productList.treasury',
                  defaultMessage: 'Treasury',
                })}
                icon="treasury"
                link="/docs/treasury"
                description={defineMessage({
                  id: 'docsApp.components.contrib.productList.bankingAAS',
                  defaultMessage: 'Banking-as-a-service',
                })}
              />
            )}
            <ProductListItem
              header={defineMessage({
                id: 'docsApp.components.contrib.productList.capital',
                defaultMessage: 'Capital',
              })}
              icon="capital"
              link="/docs/capital/faqs"
              description={defineMessage({
                id: 'docsApp.components.contrib.productList.businessFinancing',
                defaultMessage: 'Business financing',
              })}
            />
            {showCorporateCard && (
              <ProductListItem
                header={defineMessage({
                  id: 'docsApp.components.contrib.productList.corporateCard',
                  defaultMessage: 'Corporate Card',
                })}
                icon="corporatecard"
                link="/docs/corporate-card"
                description={defineMessage({
                  id: 'docsApp.components.contrib.productList.spendManagement',
                  defaultMessage: 'Spend management',
                })}
              />
            )}
            {showOpal && (
              <ProductListItem
                header={defineMessage({
                  id: 'docsApp.components.contrib.productList.opal',
                  defaultMessage: 'Opal',
                })}
                icon="bank"
                link="/docs/opal/faqs"
                description={defineMessage({
                  id: 'docsApp.components.contrib.productList.moneySAE',
                  defaultMessage: 'Money storage and expenses',
                })}
              />
            )}
          </ProductListSection>
        </Box>
      </Group>
    </Box>
  );
};
